<template>
  <component
    :is="clickable ? 'button' : 'div'"
    class="color-chip"
    :class="chipClasses"
    :style="chipStyles"
    :type="clickable ? 'button' : undefined"
    @click="handleClick"
  >
    <vs-icon
      v-if="prefixIcon"
      :icon="prefixIcon"
      :icon-pack="iconPack"
      size="20px"
    />
    <span v-if="label" class="chip-label" :style="labelStyles">{{
      label
    }}</span>
  </component>
</template>

<script>
export default {
  name: "ColorChip",
  props: {
    color: {
      type: String,
      default: "#039BE5",
    },
    label: {
      type: String,
      default: "",
    },
    // 角丸のサイズを指定（px単位の数値、または'none'で角丸なし）
    round: {
      type: [Number, String],
      default: null, // nullの場合はデフォルトの4px
    },
    // プレフィックスアイコン（vs-iconのicon名）
    prefixIcon: {
      type: String,
      default: "",
    },
    // アイコンパック
    iconPack: {
      type: String,
      default: "material-icons",
    },
    // クリック可能にするかどうか
    clickable: {
      type: Boolean,
      default: false,
    },
    // バリアント（warning, cautionなど）
    variant: {
      type: String,
      default: "",
      validator: value => ["", "warning", "caution"].includes(value),
    },
    // 背景色を直接指定（variantが指定されている場合は無視される）
    backgroundColor: {
      type: String,
      default: "",
    },
    // テキスト色を直接指定（variantが指定されている場合は無視される）
    textColor: {
      type: String,
      default: "",
    },
  },
  computed: {
    colorHex() {
      // variantが指定されている場合は無視
      if (this.variant) {
        return "";
      }
      // backgroundColorが指定されている場合はそれを使用
      if (this.backgroundColor) {
        return this.backgroundColor;
      }
      // デフォルトはcolor prop
      return this.color;
    },
    chipClasses() {
      return {
        "has-label": this.label,
        "is-round": this.round !== null && this.round !== "none",
        "is-clickable": this.clickable,
        [`is-${this.variant}`]: this.variant,
      };
    },
    chipStyles() {
      const styles = {};

      // 背景色
      if (this.colorHex) {
        styles.background = this.colorHex;
      }

      // テキスト色（variantが指定されていない場合のみ）
      if (!this.variant && this.textColor) {
        styles.color = this.textColor;
      }

      // 角丸
      if (this.round !== null) {
        if (this.round === "none") {
          styles.borderRadius = "0";
        } else {
          styles.borderRadius = `${this.round}px`;
        }
      }

      return styles;
    },
    labelStyles() {
      // variantが指定されている場合はスタイルを適用しない（CSSで処理）
      if (this.variant) {
        return {};
      }
      // textColorが指定されている場合、ラベルテキストにも適用
      if (this.textColor) {
        return {
          color: this.textColor,
        };
      }
      return {};
    },
  },
  methods: {
    handleClick(event) {
      if (this.clickable) {
        this.$emit("click", event);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.color-chip {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  border: none;
  outline: none;
  box-sizing: border-box;

  &.has-label {
    min-width: auto;
    max-width: none;
    padding: 6px 16px;
    border-radius: 4px;
    height: auto;
  }

  &.is-clickable {
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .chip-label {
    color: #fff;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 14px;
    white-space: nowrap;
  }

  // バリアントスタイル
  &.is-warning {
    background-color: #fff8e3 !important;
    color: #ffb300 !important;

    .chip-label {
      color: #ffb300;
    }
  }

  &.is-caution {
    background-color: #ffebee !important;
    color: #c62828 !important;

    .chip-label {
      color: #c62828;
    }
  }
}
</style>
